import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { useSoilStore } from './soil.store';
import type { SoilLayerResponse, SoilSocStatsResponse } from './soil.types';

/**
 * ISRIC SOC Stock (continuous, t/ha). Layer metadata and statistics are served by the
 * feasibility API; the returned tile URL stays on the analytics host and is loaded
 * through the authenticated raster tile loader.
 */
@Composable()
export class useSoilService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useSoilStore())
  public readonly soilStore!: ReturnType<typeof useSoilStore>;

  /** Tile URL template for the SOC Stock layer. Cached (single epoch). */
  public async getSoilSocLayer(signal?: AbortSignal): Promise<ApiResponse<SoilLayerResponse>> {
    const cached = this.soilStore.getSocLayer();
    if (cached) {
      return cached;
    }

    const res = await this.api.get<SoilLayerResponse>(
      `${import.meta.env.VITE_FEASIBILITY_API}/soil/soc/layer`,
      { signal },
    );

    if (res.isSuccess && res.data) {
      this.soilStore.setSocLayer(res);
    }
    return res;
  }

  /** Scalar SOC summary (mean/min/max/stdDev, t/ha) inside `coords`. */
  public async getSoilSocStats(
    coords: MultiPolygonCoords,
    signal?: AbortSignal,
  ): Promise<ApiResponse<SoilSocStatsResponse>> {
    return this.api.post<SoilSocStatsResponse>(
      `${import.meta.env.VITE_FEASIBILITY_API}/soil/soc/stats`,
      { data: coords },
      { signal },
    );
  }
}
